import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, ArrowRight, Home } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (sessionId) {
        try {
          const response = await axios.get(`${API}/payments/status/${sessionId}`);
          setPaymentInfo(response.data);
        } catch (error) {
          console.error('Error verifying payment:', error);
        } finally {
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center px-4">
      <div className="max-w-2xl w-full">
        {/* Success Icon */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-24 h-24 bg-gradient-to-br from-green-500 to-emerald-600 rounded-full mb-6 animate-bounce">
            <CheckCircle className="w-16 h-16 text-white" />
          </div>
          
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Paiement Réussi ! 🎉
          </h1>
          
          <p className="text-xl text-gray-300">
            Merci pour votre confiance. Votre réservation est confirmée.
          </p>
        </div>

        {/* Payment Details Card */}
        {loading ? (
          <div className="bg-gray-800/50 rounded-2xl p-8 border border-green-500/30 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
            <p className="text-gray-400 mt-4">Vérification du paiement...</p>
          </div>
        ) : paymentInfo ? (
          <div className="bg-gray-800/50 rounded-2xl p-8 border border-green-500/30 mb-8">
            <h2 className="text-2xl font-semibold text-green-400 mb-6">
              Détails du Paiement
            </h2>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Statut</span>
                <span className="text-green-400 font-semibold">✓ Payé</span>
              </div>
              
              <div className="flex justify-between items-center py-3 border-b border-gray-700">
                <span className="text-gray-400">Montant</span>
                <span className="text-white font-bold text-2xl">
                  {paymentInfo.amount_total}€
                </span>
              </div>
              
              {paymentInfo.booking_id && (
                <div className="flex justify-between items-center py-3 border-b border-gray-700">
                  <span className="text-gray-400">Référence Réservation</span>
                  <span className="text-white font-mono">{paymentInfo.booking_id}</span>
                </div>
              )}
              
              <div className="flex justify-between items-center py-3">
                <span className="text-gray-400">ID Transaction</span>
                <span className="text-white font-mono text-sm">
                  {sessionId?.substring(0, 20)}...
                </span>
              </div>
            </div>

            <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
              <p className="text-green-400 text-sm">
                ✓ Un email de confirmation a été envoyé à votre adresse email.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-gray-800/50 rounded-2xl p-8 border border-green-500/30 mb-8">
            <h2 className="text-2xl font-semibold text-green-400 mb-4">
              Paiement Confirmé
            </h2>
            <p className="text-gray-300">
              Votre paiement a été traité avec succès. Vous recevrez un email de confirmation sous peu.
            </p>
          </div>
        )}

        {/* Next Steps */}
        <div className="bg-gray-800/30 rounded-2xl p-6 border border-gray-700 mb-8">
          <h3 className="text-xl font-semibold text-white mb-4">
            Prochaines Étapes
          </h3>
          <ul className="space-y-3 text-gray-300">
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <span>Votre réservation est maintenant confirmée et enregistrée</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <span>Vous recevrez un email avec tous les détails de votre trajet</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
              <span>Notre chauffeur vous contactera 24h avant votre rendez-vous</span>
            </li>
          </ul>
        </div>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            onClick={() => navigate('/')}
            size="lg"
            className="flex-1 bg-gradient-to-r from-gold to-amber-600 hover:from-gold/90 hover:to-amber-600/90 text-black font-semibold"
          >
            <Home className="w-5 h-5 mr-2" />
            Retour à l'Accueil
          </Button>
          
          <Button
            onClick={() => navigate('/')}
            size="lg"
            variant="outline"
            className="flex-1 border-gold/30 text-gold hover:bg-gold/10"
          >
            <ArrowRight className="w-5 h-5 mr-2" />
            Nouvelle Réservation
          </Button>
        </div>

        {/* Support Info */}
        <div className="mt-8 text-center text-sm text-gray-400">
          <p>
            Besoin d'aide ? Contactez-nous au{' '}
            <a href="tel:+3223150254" className="text-gold hover:underline">
              +32 2 315 02 54
            </a>
            {' '}ou par email{' '}
            <a href="mailto:contact@luxurytransferservices.be" className="text-gold hover:underline">
              contact@luxurytransferservices.be
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
